import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import DettagliStorico from './DetagliStorico';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';


// ─── Utility Funkcije ────────────────────────────────────────────────────────

const getMovimentoConfig = (tipo, qta) => {
  const t = tipo?.toLowerCase() || '';
  const kolicina = Number(qta) || 0;
  const isScaricato = kolicina < 0 || t.includes('scaricato') || t.includes('-');
  const isCaricato = kolicina > 0 || t.includes('caricato') || t.includes('+');
  if (isScaricato) return { color: 'text-red-500', badge: 'bg-red-500/10 border-red-500/30 text-red-500', symbol: '' };
  if (isCaricato)  return { color: 'text-emerald-500', badge: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500', symbol: '+' };
  return { color: 'text-slate-400', badge: 'bg-slate-400/10 border-slate-400/30 text-slate-400', symbol: '' };
};

const exportCSV = (data) => {
  if (!data || data.length === 0) return;

  // Dodata Nota u zaglavlje
  const headers = ['Data', 'EAN', 'Articolo', 'Q.tà Attuale', 'Movimentato', 'Tipo', 'Utente', 'Nota'];
  
  const rows = data.map(r => [
    r.data ? new Date(r.data).toLocaleDateString('it-IT') : '',
    r.ean ?? '', 
    r.nome ?? '', 
    r.qtapresente ?? 0,
    r.qtamovimentata ?? 0, 
    r.tipomovimento ?? '', 
    r.utente ?? '',
    r.nota ?? r.note ?? '' // Dodata Nota na kraj
  ]);

  // Mapiranje u CSV format sa navodnicima radi sigurnosti (zbog zareza u notama)
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a'); 
  a.href = URL.createObjectURL(blob);
  a.download = `storico_cco_${new Date().toISOString().slice(0,10)}.csv`; 
  a.click();
};

const exportExcel = async (data) => {
  if (!data || data.length === 0) return;

  try {
    const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
    
    const ws = XLSX.utils.json_to_sheet(data.map(r => ({
      'Data': r.data ? new Date(r.data).toLocaleDateString('it-IT') : '',
      'EAN': r.ean ?? '',
      'Articolo': r.nome ?? '',
      'Q.tà Attuale': r.qtapresente ?? 0,
      'Movimento': r.qtamovimentata ?? 0,
      'Tipo': r.tipomovimento ?? '',
      'Utente': r.utente ?? '',
      'Nota': r.nota ?? r.note ?? '' // Dodata Nota na kraj
    })));

    // Postavljanje širine kolona da bi Excel izgledao profesionalno
    const wscols = [
      {wch: 12}, // Data
      {wch: 15}, // EAN
      {wch: 40}, // Articolo
      {wch: 12}, // Q.ta Attuale
      {wch: 12}, // Movimento
      {wch: 20}, // Tipo
      {wch: 20}, // Utente
      {wch: 50}  // Nota
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Storico CCO');
    XLSX.writeFile(wb, `storico_cco_${new Date().toISOString().slice(0,10)}.xlsx`);
    
  } catch (err) { 
    console.error("Excel Error:", err);
    alert('Errore tokom eksportovanja Excel fajla.'); 
  }
};

const exportPDF = (data) => {
  if (!data || data.length === 0) return;

  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const now = new Date().toLocaleString('it-IT');

    // --- HEADER ---
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42); 
    doc.text('ORIZONDESK', 14, 20);
    
    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('STORICO MOVIMENTI CCO', 14, 28);

    doc.setFontSize(9);
    doc.text(`Record esportati: ${data.length}  |  Data stampa: ${now}`, 14, 34);

    // --- PRIPREMA PODATAKA ---
    const tableData = data.map(r => {
      const conf = getMovimentoConfig(r.tipomovimento, r.qtamovimentata);
      return [
        r.data ? new Date(r.data).toLocaleDateString('it-IT') : '---',
        r.ean ?? '---',
        r.nome ?? '---',
        r.qtapresente ?? 0,
        `${conf.symbol}${r.qtamovimentata ?? 0}`,
        r.tipomovimento ?? '---',
        r.nota ?? r.note ?? '' 
      ];
    });

    // --- GENERISANJE TABELE ---
    autoTable(doc, {
      startY: 40,
      head: [['Data', 'EAN Code', 'Articolo', 'Q.tà Attuale', 'Mov.', 'Tipo Movimento', 'Nota']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [15, 23, 42], 
        fontSize: 9, 
        fontStyle: 'bold',
        halign: 'center' 
      },
      styles: { 
        fontSize: 8, 
        cellPadding: 3, 
        overflow: 'linebreak' 
      },
      columnStyles: {
        0: { cellWidth: 25 }, 
        1: { cellWidth: 35 }, 
        2: { cellWidth: 'auto' }, 
        3: { cellWidth: 25, halign: 'center' }, 
        4: { cellWidth: 20, halign: 'center', fontStyle: 'bold' }, 
        5: { cellWidth: 35 }, 
        6: { cellWidth: 50 }  
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 4) {
          const val = data.cell.raw.toString();
          if (val.startsWith('+')) {
            data.cell.styles.textColor = [16, 185, 129]; 
          } else if (val.startsWith('-')) {
            data.cell.styles.textColor = [239, 68, 68];  
          }
        }
      },
      didDrawPage: function(data) {
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont('helvetica', 'normal');

        // LEVO: Datum štampe
        doc.text(`Stampato il: ${now}`, 14, pageHeight - 10);

        // SREDINA: Naziv + Stranica
        const middleText = `OrizonDesk - Pagina ${doc.internal.getNumberOfPages()}`;
        doc.text(middleText, pageWidth / 2, pageHeight - 10, { align: 'center' });

        // DESNO: URL sajta
        const url = "https://desk.orizon-aix.com";
        doc.text(url, pageWidth - 14, pageHeight - 10, { align: 'right' });
      }
    });

    doc.save(`Storico_CCO_${new Date().toISOString().slice(0,10)}.pdf`);

  } catch (error) {
    console.error("PDF Error:", error);
    alert("Errore tecnico durante l'esportacija PDF.");
  }
};

// ─── Konfiguracija Kolona ─────────────────────────────────────────────────────

const COLONNE = [
  { label: 'Data',           key: 'data',           width: 110, align: 'left'   },
  { label: 'EAN Code',       key: 'ean',            width: 140, align: 'left'   },
  { label: 'Articolo',       key: 'nome',           width: 240, align: 'left'   },
  { label: 'Q.tà Attuale',   key: 'qtapresente',    width: 110, align: 'center' },
  { label: 'Q.tà Movimenta', key: 'qtamovimentata', width: 100, align: 'center' },
  { label: 'Tipo Movimento', key: 'tipomovimento',  width: 160, align: 'center' },
  { label: '',               key: '__detail',       width: 80,  align: 'center', noSort: true },
];

const DENSITA = { compatta: 'py-2', normale: 'py-4', spaziosa: 'py-6' };

// Kolone filtrabili — sve osim __detail i numeričkih koje nemaju smisla kao dropdown
const FILTRO_COLONNE = COLONNE.filter(c => !['__detail', 'qtapresente', 'qtamovimentata'].includes(c.key));


// ─── ImpostazioniModal — 3 taba identično kao cilindripaniera ─────────────────

const ImpostazioniModal = ({
  colVisibili, setColVisibili,
  densita, setDensita,
  filtri, setFiltri,
  valoriDistinti,
  onClose,
}) => {
  const [tab, setTab] = useState('colonne'); // 'colonne' | 'filtri' | 'densita'

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const addFiltro = () => {
    if (filtri.length >= 4) return;
    setFiltri(prev => [...prev, { col: '', val: '', dateFrom: '', dateTo: '' }]);
  };

  const removeFiltro = (i) => setFiltri(prev => prev.filter((_, idx) => idx !== i));

  const updateFiltro = (i, field, value) => {
    setFiltri(prev => prev.map((f, idx) =>
      idx === i ? { ...f, [field]: value, ...(field === 'col' ? { val: '', dateFrom: '', dateTo: '' } : {}) } : f
    ));
  };

  const isDateCol = (col) => col === 'data';

  const activeFiltriCount = filtri.filter(f => f.col && (f.val || f.dateFrom || f.dateTo)).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="relative w-full max-w-sm bg-card border-2 border-border/60 rounded-3xl shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-hidden flex flex-col max-h-[85vh]">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/60 to-transparent shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/40 shrink-0">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mb-0.5">Pannello</p>
            <h3 className="text-lg font-black uppercase tracking-tight">Impostazioni</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-muted/60 hover:bg-red-500/10 hover:text-red-500 border border-border/40 flex items-center justify-center transition-all text-muted-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex shrink-0 border-b border-border/40 px-2 pt-2">
          {[
            { id: 'colonne', label: 'Colonne',  icon: 'Columns'       },
            { id: 'filtri',  label: `Filtri${activeFiltriCount > 0 ? ` (${activeFiltriCount})` : ''}`, icon: 'Filter' },
            { id: 'densita', label: 'Densità',  icon: 'AlignJustify'  },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 text-[10px] font-black uppercase tracking-widest rounded-t-xl transition-all duration-200 border-b-2
                ${tab === t.id
                  ? 'text-primary border-primary bg-primary/5'
                  : 'text-muted-foreground/50 border-transparent hover:text-muted-foreground'
                }`}
            >
              <Icon name={t.icon} size={11} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenuto tab */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>

          {/* TAB: Colonne */}
          {tab === 'colonne' && (
            <div className="flex flex-col gap-2">
              {COLONNE.filter(c => c.key !== '__detail').map(col => (
                <label key={col.key} className="flex items-center justify-between cursor-pointer group">
                  <span className="text-sm font-bold text-foreground/70 group-hover:text-foreground transition-colors">{col.label}</span>
                  <div
                    onClick={() => setColVisibili(prev => ({ ...prev, [col.key]: prev[col.key] === false }))}
                    className={`relative w-10 h-5 rounded-full transition-all duration-300 ${colVisibili[col.key] !== false ? 'bg-primary' : 'bg-muted/80 border border-border/60'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-300 ${colVisibili[col.key] !== false ? 'left-5' : 'left-0.5'}`} />
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* TAB: Filtri */}
          {tab === 'filtri' && (
            <div className="flex flex-col gap-4">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50">
                Aggiungi fino a 4 filtri. Per la data seleziona un intervallo.
              </p>

              {filtri.map((f, i) => (
                <div key={i} className="flex flex-col gap-2.5 p-4 rounded-2xl bg-muted/20 border border-border/30">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-widest text-primary/60">Filtro {i + 1}</span>
                    <button
                      onClick={() => removeFiltro(i)}
                      className="w-6 h-6 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all"
                    >
                      <Icon name="X" size={11} />
                    </button>
                  </div>

                  {/* Selezione colonna */}
                  <select
                    value={f.col}
                    onChange={(e) => updateFiltro(i, 'col', e.target.value)}
                    className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                  >
                    <option value="">— Seleziona colonna —</option>
                    {FILTRO_COLONNE.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>

                  {/* Valore filtro — dropdown dinamico dai dati */}
                  {f.col && !isDateCol(f.col) && (
                    <select
                      value={f.val}
                      onChange={(e) => updateFiltro(i, 'val', e.target.value)}
                      className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                    >
                      <option value="">— Tutti —</option>
                      {(valoriDistinti[f.col] || []).map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  )}

                  {/* Date range — solo per colonna 'data' */}
                  {f.col && isDateCol(f.col) && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase text-muted-foreground/50 w-6 shrink-0">Da</span>
                        <input
                          type="date"
                          value={f.dateFrom}
                          onChange={(e) => updateFiltro(i, 'dateFrom', e.target.value)}
                          className="flex-1 bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase text-muted-foreground/50 w-6 shrink-0">A</span>
                        <input
                          type="date"
                          value={f.dateTo}
                          onChange={(e) => updateFiltro(i, 'dateTo', e.target.value)}
                          className="flex-1 bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {filtri.length < 4 && (
                <button
                  onClick={addFiltro}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/40 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground/40 hover:text-primary transition-all duration-200 text-[11px] font-black uppercase tracking-widest"
                >
                  <Icon name="Plus" size={14} />
                  Aggiungi filtro
                </button>
              )}

              {filtri.length > 0 && (
                <button
                  onClick={() => setFiltri([])}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 text-red-500/70 hover:text-red-500 transition-all text-[11px] font-black uppercase tracking-widest"
                >
                  <Icon name="Trash2" size={12} />
                  Azzera tutti i filtri
                </button>
              )}
            </div>
          )}

          {/* TAB: Densità */}
          {tab === 'densita' && (
            <div className="flex flex-col gap-3">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50 mb-1">Spaziatura righe</p>
              {Object.keys(DENSITA).map(d => (
                <button
                  key={d}
                  onClick={() => setDensita(d)}
                  className={`flex items-center justify-between px-4 py-3 rounded-2xl border text-sm font-black uppercase tracking-wider transition-all duration-200
                    ${densita === d
                      ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                      : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-primary/30'
                    }`}
                >
                  <span>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                  {densita === d && <Icon name="Check" size={14} />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-muted/40 border-t border-border/40 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary rounded-xl text-[11px] font-black uppercase tracking-widest transition-all"
          >
            Fatto
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── FiltriBadge ──────────────────────────────────────────────────────────────

const FiltriBadge = ({ filtri, onRemove }) => {
  const attivi = filtri.filter(f => f.col && (f.val || f.dateFrom || f.dateTo));
  if (attivi.length === 0) return null;
  const getLabelCol = (key) => {
    const found = COLONNE.find(c => c.key === key);
    return found?.label || key;
  };
  return (
    <div className="flex flex-wrap gap-2 px-1 mb-3 shrink-0">
      {attivi.map((f, i) => {
        const label = getLabelCol(f.col);
        const val = f.val || `${f.dateFrom || '…'} → ${f.dateTo || '…'}`;
        return (
          <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <Icon name="Filter" size={10} />
            <span className="text-[10px] font-black uppercase tracking-widest">{label}: {val}</span>
            <button
              onClick={() => onRemove(i)}
              className="ml-1 w-4 h-4 rounded-full bg-primary/20 hover:bg-primary/40 flex items-center justify-center transition-all"
            >
              <Icon name="X" size={9} />
            </button>
          </div>
        );
      })}
    </div>
  );
};


// ─── ExportDropdown ───────────────────────────────────────────────────────────

const ExportDropdown = ({ data }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/80 border border-border/40 hover:border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all duration-200 shadow-sm"
      >
        <Icon name="Download" size={14} />
        <span className="hidden sm:inline">Esporta</span>
        <Icon name="ChevronDown" size={12} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-card border-2 border-border/60 rounded-2xl shadow-2xl overflow-hidden z-20 animate-in fade-in zoom-in-95 duration-150">
          <button onClick={() => exportCSV(data)} className="w-full flex items-center gap-3 px-4 py-3 text-[12px] font-bold text-foreground/70 hover:text-foreground hover:bg-primary/5 transition-all border-b border-border/20 last:border-0">
            <Icon name="FileText" size={14} className="text-primary/60" /> Esporta CSV
          </button>
          <button onClick={() => exportExcel(data)} className="w-full flex items-center gap-3 px-4 py-3 text-[12px] font-bold text-foreground/70 hover:text-foreground hover:bg-primary/5 transition-all border-b border-border/20 last:border-0">
            <Icon name="Sheet" size={14} className="text-primary/60" /> Esporta Excel
          </button>
          <button onClick={() => exportPDF(data)} className="w-full flex items-center gap-3 px-4 py-3 text-[12px] font-bold text-foreground/70 hover:text-foreground hover:bg-primary/5 transition-all border-b border-border/20 last:border-0">
            <Icon name="FileDown" size={14} className="text-primary/60" /> Esporta PDF
          </button>
        </div>
      )}
    </div>
  );
};


// ─── Glavna Komponenta ────────────────────────────────────────────────────────

const StoricoCCO = () => {
  const [storico, setStorico]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [sortConfig, setSortConfig]         = useState({ key: 'data', direction: 'desc' });
  const [displayLimit, setDisplayLimit]     = useState(40);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [showSettings, setShowSettings]     = useState(false);
  const [densita, setDensita]               = useState('normale');
  const [colVisibili, setColVisibili]       = useState({});
  const [filtri, setFiltri]                 = useState([]);
  const [valoriDistinti, setValoriDistinti] = useState({});

  const scrollContainerRef = useRef(null);

  const fetchStorico = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cco_storico')
        .select('*')
        .order('data', { ascending: false });
      if (error) throw error;
      setStorico(data || []);

      // Calcola valori distinti per ogni colonna filtrabile dai dati ricevuti
      const dist = {};
      FILTRO_COLONNE.forEach(({ key }) => {
        dist[key] = [...new Set((data || []).map(r => r[key]).filter(Boolean))].sort();
      });
      setValoriDistinti(dist);
    } catch (err) {
      console.error('Errore fetch:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStorico();
    const channel = supabase
      .channel('cco_storico_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'cco_storico' },
        (payload) => setStorico(prev => [payload.new, ...prev])
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchStorico]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const allFiltered = storico
    .filter(item => {
      // Search globale
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const match = [item.ean, item.nome, item.utente, item.tipomovimento]
          .some(v => v?.toString().toLowerCase().includes(s));
        if (!match) return false;
      }

      // Filtri aggiuntivi da tab Filtri
      for (const f of filtri) {
        if (!f.col) continue;

        const isDate = f.col === 'data';

        if (isDate) {
          if (!f.dateFrom && !f.dateTo) continue;
          const val = item[f.col] ? new Date(item[f.col]) : null;
          if (!val) return false;
          if (f.dateFrom && val < new Date(f.dateFrom)) return false;
          if (f.dateTo) {
            const toDate = new Date(f.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (val > toDate) return false;
          }
        } else {
          if (!f.val) continue;
          const itemVal = (item[f.col] ?? '').toString().toLowerCase();
          if (!itemVal.includes(f.val.toLowerCase())) return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const av = a[sortConfig.key] ?? '', bv = b[sortConfig.key] ?? '';
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const visibleData = allFiltered.slice(0, displayLimit);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 120 && displayLimit < allFiltered.length) {
      setDisplayLimit(prev => prev + 30);
    }
  }, [displayLimit, allFiltered.length]);

  useEffect(() => {
    setDisplayLimit(40);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [searchTerm, filtri]);

  const removeFiltro = (i) => setFiltri(prev => prev.filter((_, idx) => idx !== i));

  const colAttive = COLONNE.filter(c => c.key === '__detail' || colVisibili[c.key] !== false);
  const tableMinWidth = colAttive.reduce((acc, c) => acc + c.width, 0);
  const padClass = DENSITA[densita];
  const activeFiltriCount = filtri.filter(f => f.col && (f.val || f.dateFrom || f.dateTo)).length;

  return (
    <>
      <div className="w-full h-screen flex flex-col p-3 md:p-6 animate-in fade-in duration-700 overflow-hidden bg-background">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4 md:mb-6 px-1 shrink-0">
          <div>
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
                STORICO<span className="text-primary">MOVIMENTI</span>
              </h2>
              <span className="text-[10px] font-black uppercase tracking-[.25em] text-muted-foreground/40 pb-1">CCO</span>
            </div>
            <div className="flex items-center gap-2 mt-2 md:mt-3">
              <span className="h-[2px] w-6 bg-primary rounded-full" />
              <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">Registro cronologico magazzino</p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto flex-wrap">
            <div className="relative flex-1 min-w-0">
              <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary/60" />
              <input
                type="text"
                placeholder="Cerca EAN, Articolo, Utente..."
                className="w-full bg-muted/40 border border-border/40 rounded-2xl py-2.5 pl-9 pr-8 outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/10 transition-all text-sm font-semibold shadow-sm"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <ExportDropdown data={allFiltered} />
            <button
              onClick={() => setShowSettings(true)}
              className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm
                ${activeFiltriCount > 0
                  ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
                  : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
                }`}
            >
              <Icon name={activeFiltriCount > 0 ? 'Filter' : 'Settings2'} size={14} />
              <span className="hidden sm:inline">
                {activeFiltriCount > 0 ? `Filtri (${activeFiltriCount})` : 'Impostazioni'}
              </span>
              {activeFiltriCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-black flex items-center justify-center">
                  {activeFiltriCount}
                </span>
              )}
            </button>
            <button
              onClick={fetchStorico}
              disabled={loading}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all"
            >
              <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Badge filtri attivi */}
        {activeFiltriCount > 0 && (
          <FiltriBadge filtri={filtri} onRemove={removeFiltro} />
        )}

        {/* Tabella */}
        <div className="flex-1 bg-card border border-border/40 rounded-3xl shadow-xl overflow-hidden flex flex-col min-h-0">
          <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-auto min-h-0">
            <table className="border-collapse" style={{ minWidth: tableMinWidth, width: '100%' }}>
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted/90 backdrop-blur-sm border-b border-border/50">
                  {colAttive.map((col, i) => (
                    <th
                      key={col.key}
                      onClick={() => !col.noSort && handleSort(col.key)}
                      style={{ width: col.width, minWidth: col.width }}
                      className={`px-4 py-3.5 text-[10px] font-black uppercase tracking-[.18em] transition-colors whitespace-nowrap
                        ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}
                        ${!col.noSort ? 'cursor-pointer hover:text-primary' : ''}
                        ${sortConfig.key === col.key ? 'text-primary' : 'text-muted-foreground/60'}
                        ${i === 0 ? 'pl-5' : ''} ${i === colAttive.length - 1 ? 'pr-5' : ''}`}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        {col.label}
                        {!col.noSort && sortConfig.key === col.key && (
                          <Icon name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={10} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={colAttive.length} className="py-40 text-center"><div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" /></td></tr>
                ) : visibleData.length === 0 ? (
                  <tr>
                    <td colSpan={colAttive.length} className="py-32 text-center">
                      <div className="inline-flex flex-col items-center gap-3">
                        <Icon name="PackageSearch" size={36} className="text-muted-foreground/20" />
                        <span className="text-sm font-bold text-muted-foreground/40">Nessun movimento trovato</span>
                        <button
                          onClick={() => { setSearchTerm(''); setFiltri([]); }}
                          className="text-[10px] font-black uppercase tracking-widest text-primary/60 hover:text-primary transition-colors"
                        >
                          Azzera ricerca e filtri
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : visibleData.map((item) => {
                  const movConf = getMovimentoConfig(item.tipomovimento, item.qtamovimentata);
                  return (
                    <tr key={item.id} className="group border-b border-border/[0.15] transition-all hover:bg-primary/[0.05]">
                      {colVisibili['data'] !== false && (
                        <td className={`px-5 ${padClass} font-bold text-[12px] text-muted-foreground/60`}>
                          {item.data ? new Date(item.data).toLocaleDateString('it-IT') : '---'}
                        </td>
                      )}
                      {colVisibili['ean'] !== false && (
                        <td className={`px-4 ${padClass} font-mono text-[12px] text-muted-foreground/30`}>{item.ean}</td>
                      )}
                      {colVisibili['nome'] !== false && (
                        <td className={`px-4 ${padClass} text-[13px] font-black uppercase tracking-tight text-foreground/75`}>{item.nome}</td>
                      )}
                      {colVisibili['qtapresente'] !== false && (
                        <td className={`px-4 ${padClass} text-center font-bold text-muted-foreground/40`}>{item.qtapresente}</td>
                      )}
                      {colVisibili['qtamovimentata'] !== false && (
                        <td className={`px-4 ${padClass} text-center`}>
                          <span className={`px-2 py-1 rounded-lg text-[14px] font-black ${movConf.color}`}>
                            {movConf.symbol}{item.qtamovimentata}
                          </span>
                        </td>
                      )}
                      {colVisibili['tipomovimento'] !== false && (
                        <td className={`px-4 ${padClass} text-center`}>
                          <div className={`inline-flex px-3 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest shadow-sm ${movConf.badge}`}>
                            {item.tipomovimento}
                          </div>
                        </td>
                      )}
                      <td className={`px-3 ${padClass}`} style={{ width: 80 }}>
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => setSelectedItem(item)}
                            className="inline-flex items-center justify-center gap-1.5 w-10 h-10 md:w-auto md:px-5 md:py-2 rounded-2xl bg-primary/5 border border-primary/20 text-primary/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:shadow-[0_0_15px_rgba(var(--primary-rgb),0.4)] hover:scale-105 active:scale-95 transition-all duration-300 text-[10px] font-black uppercase tracking-[.15em]"
                          >
                            <Icon name="Eye" size={15} strokeWidth={3} />
                            <span className="hidden md:inline">Apri</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {displayLimit < allFiltered.length && (
              <div className="py-4 text-center" style={{ minWidth: tableMinWidth }}>
                <span className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/30">
                  Scorri per caricare altri — {allFiltered.length - displayLimit} rimanenti
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSettings && (
        <ImpostazioniModal
          colVisibili={colVisibili}
          setColVisibili={setColVisibili}
          densita={densita}
          setDensita={setDensita}
          filtri={filtri}
          setFiltri={setFiltri}
          valoriDistinti={valoriDistinti}
          onClose={() => setShowSettings(false)}
        />
      )}

      {selectedItem && (
        <DettagliStorico
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={(updated) => setStorico(prev => prev.map(s => s.id === updated.id ? updated : s))}
        />
      )}
    </>
  );
};

export default StoricoCCO;